import { FC, ElementType } from 'react';
import classNames from 'classnames';
import getClassNames from 'src/common/utils/getClassNames';
import { withDesignSystemStyles, type DesignSystemStylesProps } from 'src/common/components/WithDesignSystemStyles';
import styles from './ContentGroup.module.scss';

export const variants = [
  'withNoSpacing',
  'error',
  /**
   * `withPreviousLayout` variant was introduced to preserve initial layout of ContentGroup.
   * Because a lot of existing components rely on that layout (e.g. direct-booking components)
   */
  'withPreviousLayout',
] as const;
export type Variant = typeof variants[number];
export type ContentGroupProps = {
  /** Defines the border style of the content group. */
  border?: 'none' | 'bottom' | 'top';
  /** The content to be rendered within the group. */
  children: React.ReactNode;
  /** CSS class name(s) from the HOC to apply to the component. */
  className?: string;
  display?: 'block' | 'flex' | 'grid' | 'inline-block' | 'inline-flex';
  renderAs?: ElementType;
  /** Visual variant(s) to apply to the content group. */
  variant?: Variant | Variant[];
  /** Defines whether the content should wrap or not. */
  wrap?: boolean;
} & DesignSystemStylesProps;

const ContentGroup: FC<ContentGroupProps> = ({
  border,
  children,
  className: classNameFromHoc,
  display,
  renderAs: Wrapper = 'div',
  variant,
  wrap,
}) => (
  <Wrapper className={classNames(getClassNames(styles, 'root', classNames(
    border && `border-${border}`,
    display && `display-${display}`,
    wrap === true && 'wrap',
    wrap === false && 'no-wrap',
    variant,
  )), classNameFromHoc)}
  >
    {children}
  </Wrapper>
);

export default withDesignSystemStyles(ContentGroup) as FC<ContentGroupProps>;
